import React, { useState, useReducer, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { getPatternFormat, useAPIWrapper } from 'drf-react-by-schema';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { PatternFormat } from 'react-number-format';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import '../styles/globals.css';
import '../styles/login.css';

import asmubip from '../assets/images/logoASMUBIP5.png';
import logo from '../assets/images/LOGO-APP-312.png';
import footer from '../assets/images/assinatura-serpro.png';

import Box from '@mui/material/Box';

import { Layout } from 'src/styles';
import { LoginPage, SpaceBetweenHolder } from 'src/styles/styledComponents';
import AsmubipButton from 'src/components/AsmubipButton';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

type LoginPayload = {
  username: null | string;
  password: null | string;
};

type Status = 'loggedIn' | 'failed' | { error: Partial<LoginPayload> } | null;

const initialPayload: LoginPayload = {
  username: null,
  password: null,
};

export default function Login() {
  const theme = useTheme();
  const [submitted, setSubmited] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const [passwordFieldType, setPasswordType] = useState<'password' | undefined>('password');
  const reducer = (state: LoginPayload, action: Partial<LoginPayload>) => {
    return { ...state, ...action };
  };
  const [payload, setPayload] = useReducer(reducer, initialPayload);
  const { handleLoading, updateUsuaria, loginByPayload } = useAPIWrapper();
  const handleSubmit = () => {
    loginByPayload({ ...payload, username: payload.username?.toString() }).then((success) => {
      if (success) {
        setStatus('loggedIn');
        updateUsuaria();
        return;
      }
      setStatus('failed');
      setSubmited(true);
    });
  };

  useEffect(() => {
    setStatus(null);
    setPayload(initialPayload);
    handleLoading(false);
  }, []);

  useEffect(() => {
    const checkErrors = () => {
      const newStatus: Status = { error: initialPayload };
      if (!payload.username && !payload.password) {
        setStatus(newStatus);
        return;
      }
      const newUserName = payload.username?.toString();
      const newPassword = payload.password;
      if (!newUserName) {
        newStatus.error.username = 'O número de telefone é obrigatório';
      } else if (newUserName.length !== 11) {
        newStatus.error.username = 'Telefone inválido. Favor corrigir.';
      }
      if (!newPassword) {
        newStatus.error.password = 'A senha é obrigatória';
      }
      setStatus(newStatus);
    };
    if (submitted) {
      checkErrors();
    }
  }, [payload, submitted]);

  const handleTogglePassword = () => {
    setPasswordType(passwordFieldType === 'password' ? undefined : 'password');
  };

  return (
    <>
      {status === 'loggedIn' ? (
        <Navigate to={'/'} replace />
      ) : (
        <LoginPage>
          <SpaceBetweenHolder>
            <Box sx={{ padding: '20px' }}>
              <img src={asmubip} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <img src={logo} />
              <Box sx={{ textAlign: 'center', padding: '20px' }}>
                {status === 'failed' && (
                  <Alert severity="error">
                    A autenticação falhou. Confira seu nome de usuária ou senha e tente novamente.
                  </Alert>
                )}
                <form>
                  <PatternFormat
                    autoFocus
                    margin="dense"
                    key="username"
                    label="CPF"
                    onValueChange={(values) => {
                      setPayload({ username: values.value });
                    }}
                    value={payload.username}
                    valueIsNumericString
                    format={getPatternFormat('cpf')}
                    type="tel"
                    autoComplete="username"
                    mask="_"
                    fullWidth
                    variant="filled"
                    sx={Layout.loginInput}
                    customInput={TextField}
                    error={status !== null && status !== 'failed' && !!status.error.username}
                    helperText={status !== null && status !== 'failed' && status.error.username}
                  />

                  <TextField
                    margin="dense"
                    id="password"
                    label="Senha"
                    type={passwordFieldType}
                    autoComplete="current-password"
                    fullWidth
                    variant="filled"
                    sx={Layout.loginInput}
                    onKeyUp={(e) => {
                      if (e.code === 'Enter') {
                        handleSubmit();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePassword}>
                            {passwordFieldType === 'password' ? (
                              <VisibilityIcon color="inherit" />
                            ) : (
                              <VisibilityOffIcon color="inherit" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => {
                      setPayload({ password: e.target.value });
                    }}
                    error={status !== null && status !== 'failed' && !!status.error.password}
                    helperText={status !== null && status !== 'failed' && status.error.password}
                  />
                </form>
                <AsmubipButton
                  disabled={!payload.username || !payload.password}
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  sx={{ marginTop: '40px', marginBottom: '40px' }}>
                  Entrar
                </AsmubipButton>
                <Box sx={{ color: 'secondary.contrastText' }}>
                  <Link href="/password-reset" sx={{ color: theme.palette.primary.contrastText }}>
                    Esqueci minha senha
                  </Link>
                </Box>
              </Box>
            </Box>
            <Box sx={Layout.loginFooter}>
              <img src={footer} />
            </Box>
          </SpaceBetweenHolder>
        </LoginPage>
      )}
    </>
  );
}
